import { lazy, Suspense, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FeatureErrorBoundary } from '@/core/error-boundaries/public';
import { sentryReporter } from '@/core/observability/public';
import { ConfirmDialog } from '@/shared/components/custom/confirm-dialog/ConfirmDialog';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { useConfirm, useToast } from '@/shared/hooks';
import { useWhiteboard } from '../hooks/useWhiteboard';
import { useWhiteboards } from '../hooks/useWhiteboards';
import {
  useCreateWhiteboard,
  useDeleteWhiteboard,
  useRenameWhiteboard,
} from '../hooks/useWhiteboardMutations';
import { WhiteboardEmptyState } from './WhiteboardEmptyState';
import { WhiteboardTabsBar } from './WhiteboardTabsBar';
import type { WhiteboardSummaryDto } from '../types';

/**
 * Konva and Yjs weigh far more than the rest of the board bundle and are useless until a
 * whiteboard is opened, so the canvas is the lazy boundary. Nothing reachable from
 * `public.ts` may import it statically — `bootstrap.ts` pulls that module in eagerly to
 * register translations, which would drag the canvas back into the main chunk.
 */
const WhiteboardCanvas = lazy(() => import('./WhiteboardCanvas'));

function CanvasFallback() {
  return <Skeleton className="h-full w-full" data-testid="whiteboards-canvas-fallback" />;
}

interface WhiteboardViewProps {
  projectId: string;
  boardId: string;
}

export function WhiteboardView({ projectId, boardId }: WhiteboardViewProps) {
  const { t } = useTranslation('whiteboards');
  const { toast } = useToast();

  const { whiteboards, isLoading, isError, refetch } = useWhiteboards(boardId);
  const createWhiteboard = useCreateWhiteboard(boardId);
  const renameWhiteboard = useRenameWhiteboard(boardId);
  const deleteWhiteboard = useDeleteWhiteboard(boardId);

  const [selectedId, setSelectedId] = useState<string | null>(null);

  const {
    confirm,
    isOpen: confirmOpen,
    options: confirmOptions,
    handleConfirm,
    handleCancel,
  } = useConfirm();

  // Derived rather than synced through an effect: falls back to the first whiteboard on load
  // and heals itself when the selected one is deleted by anyone, without a cascading render.
  const effectiveSelectedId =
    selectedId !== null && whiteboards.some((w) => w.id === selectedId)
      ? selectedId
      : (whiteboards[0]?.id ?? null);

  const { whiteboard, refetch: refetchWhiteboard } = useWhiteboard(effectiveSelectedId);

  /**
   * Creates a whiteboard straight away, no dialog.
   *
   * @remarks
   * Sheet tabs are renamed in place, so asking for a name up front would demand it twice. The
   * generated name counts existing whiteboards, which can collide after a deletion — harmless,
   * since names are not unique and the tab is renamed on the spot anyway.
   */
  async function handleCreate() {
    try {
      const { data } = await createWhiteboard.mutateAsync({
        name: t('tabs.defaultName', { index: whiteboards.length + 1 }),
      });
      if (data.id) setSelectedId(data.id);
    } catch {
      // global handler shows the error toast
    }
  }

  async function handleRename(whiteboardId: string, name: string) {
    try {
      await renameWhiteboard.mutateAsync({ whiteboardId, payload: { name } });
    } catch {
      // global handler shows the error toast
    }
  }

  async function handleDelete(target: WhiteboardSummaryDto) {
    const { confirmed } = await confirm({
      title: t('delete.confirmTitle'),
      description: t('delete.confirmDescription'),
      variant: 'destructive',
    });
    if (!confirmed || !target.id) return;

    try {
      await deleteWhiteboard.mutateAsync(target.id);
      toast.success({ title: t('toast.deleteSuccess') });
    } catch {
      // global handler shows the error toast
    }
  }

  const hasWhiteboards = whiteboards.length > 0;
  const showEmptyState = !isLoading && !isError && !hasWhiteboards;

  return (
    <FeatureErrorBoundary reporter={sentryReporter}>
      <div className="flex h-full flex-col" data-testid="whiteboards-view">
        <div className="relative min-h-0 flex-1">
          {showEmptyState ? (
            <WhiteboardEmptyState onCreate={() => void handleCreate()} canCreate />
          ) : (
            whiteboard && (
              <Suspense fallback={<CanvasFallback />}>
                {/* Keyed so switching whiteboards mounts a fresh document instead of
                    mutating the live one. */}
                <WhiteboardCanvas
                  key={whiteboard.id}
                  whiteboard={whiteboard}
                  projectId={projectId}
                  onRenamed={() => void refetch()}
                  onDeleted={() => void refetch()}
                  onRetry={() => void refetchWhiteboard()}
                />
              </Suspense>
            )
          )}
        </div>

        {!showEmptyState && (
          <WhiteboardTabsBar
            whiteboards={whiteboards}
            selectedId={effectiveSelectedId}
            isLoading={isLoading}
            isError={isError}
            canManage
            isCreating={createWhiteboard.isPending}
            onRetry={() => void refetch()}
            onSelect={setSelectedId}
            onCreate={() => void handleCreate()}
            onRename={(whiteboardId, name) => void handleRename(whiteboardId, name)}
            onDelete={(target) => void handleDelete(target)}
          />
        )}

        <ConfirmDialog
          open={confirmOpen}
          onOpenChange={(open) => {
            if (!open) handleCancel();
          }}
          title={confirmOptions?.title ?? ''}
          description={confirmOptions?.description}
          confirmLabel={confirmOptions?.confirmLabel}
          cancelLabel={confirmOptions?.cancelLabel}
          variant={confirmOptions?.variant}
          onConfirm={handleConfirm}
          onCancel={handleCancel}
        />
      </div>
    </FeatureErrorBoundary>
  );
}
